import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Trash2, Pencil, Eye, Code2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const SHORTCODES = ["{{full_name}}", "{{first_name}}", "{{email}}", "{{company}}"];

export default function BulkTemplates() {
  const { activeCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("visual");
  const [form, setForm] = useState({ company_id: "", name: "", subject: "", body_html: "", body_text: "", tags: [] });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const [t, c] = await Promise.all([
      api.get("/bulk/templates", { params }),
      api.get("/companies"),
    ]);
    setItems(t.data);
    setCompanies(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const openNew = () => {
    setForm({ company_id: activeCompanyId || companies[0]?.id || "", name: "", subject: "", body_html: "", body_text: "", tags: [] });
    setEditingId(null);
    setMode("visual");
    setOpen(true);
  };

  const openEdit = (t) => { setForm({ ...t }); setEditingId(t.id); setMode("visual"); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const { id: _id, company_id: _c, created_at: _ca, ...patch } = form;
        await api.patch(`/bulk/templates/${editingId}`, patch);
        toast.success("Template updated");
      } else {
        await api.post("/bulk/templates", form);
        toast.success("Template created");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await api.delete(`/bulk/templates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="bulk-templates-page">
      <div className="flex justify-end">
        <Button onClick={openNew} className="rounded-sm" data-testid="new-template-button"><Plus className="h-4 w-4 mr-1.5" /> New template</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-sm bg-emerald-brand/12 text-emerald-brand grid place-items-center"><FileText className="h-3.5 w-3.5" /></div>
                  <h3 className="font-medium truncate">{t.name}</h3>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5 truncate">{t.subject || "(no subject)"}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-crimson" onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-border bg-muted/30 p-2 text-[11px] max-h-24 overflow-hidden text-muted-foreground">
              <div className="line-clamp-4" dangerouslySetInnerHTML={{ __html: t.body_html || t.body_text?.replace(/\n/g, "<br>") || "(empty)" }} />
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-8 text-sm text-muted-foreground">No templates yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-modal">
          <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">{editingId ? "Edit" : "New"} template</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {!editingId && (
              <F label="Company">
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
            <F label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="template-name-input" /></F>
            <F label="Subject (supports shortcodes)"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="template-subject-input" placeholder="Hi {{first_name}} — a note from {{company}}" /></F>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Shortcodes:</span>
              {SHORTCODES.map((s) => (
                <button key={s} type="button" onClick={() => navigator.clipboard.writeText(s).then(() => toast.success(`Copied ${s}`))} className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted hover:bg-emerald-brand/12 hover:text-emerald-brand transition-colors" data-testid={`shortcode-${s.replace(/[{}]/g, "")}`}>{s}</button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Body</Label>
              <div className="inline-flex rounded-lg border border-border overflow-hidden text-[10px] font-mono uppercase">
                <button type="button" onClick={() => setMode("visual")} className={`px-2 py-1 flex items-center gap-1 ${mode === "visual" ? "bg-emerald-brand/15 text-emerald-brand" : "text-muted-foreground"}`}><Eye className="h-3 w-3" /> Visual</button>
                <button type="button" onClick={() => setMode("html")} className={`px-2 py-1 flex items-center gap-1 ${mode === "html" ? "bg-emerald-brand/15 text-emerald-brand" : "text-muted-foreground"}`}><Code2 className="h-3 w-3" /> HTML</button>
              </div>
            </div>
            {mode === "visual"
              ? <RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} placeholder="Design your email…" />
              : <Textarea rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="rounded-xl font-mono text-xs" data-testid="template-html-input" />
            }
            <F label="Plain text fallback (optional)"><Textarea rows={3} value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} className="rounded-xl" /></F>
            <DialogFooter><Button type="submit" className="rounded-xl" data-testid="template-submit">{editingId ? "Save" : "Create"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
